import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IndustryModeEnum } from "../../../api/constants";
import { RBAdminEmail } from "../../../util/Common";
import { rbPaySettingsService } from "../services/rbPaySettings.service";
import {
  RBPayEmploymentType,
  RBPayNetworkConfiguration,
  RBPayPaymentMethod,
  RBPaySettingsState,
  RBPayVerificationState,
} from "../types/rbPaySettings.types";
import {
  buildRBPayPaymentSectionDraft,
  buildRBPayProfileSectionDraft,
  cloneRBPaySettingsState,
  createDefaultRBPaySettingsState,
} from "../utils/rbPaySettings.mappers";
import {
  composeBusinessNumber,
  defaultBusinessNumber,
  formatSin,
  maskBusinessNumber,
  normalizeDigits,
} from "../utils/rbPaySettings.formatters";
import {
  hasValidationErrors,
  validateRBPayPaymentSection,
  validateRBPayProfileSection,
} from "../validators/rbPaySettings.validators";
import { RB_PAY_INDUSTRY_CONFIG } from "../constants/rbPaySettings.constants";

export type RBPaySetupSectionKey = "profile" | "payment" | "authorization";
export type RBPaySetupMode = "setup" | "manage";
// Outcome of a save attempt so the page can react precisely (success modal,
// scroll-to-error, or an error alert).
export type RBPaySaveResult = "saved" | "invalid" | "pending-verification" | "error";

const defaultVerificationState: RBPayVerificationState = {
  open: false,
  email: "",
  code: "",
  errorMessage: "",
  successMessage: "",
  isRequesting: false,
  isVerifying: false,
  pendingSave: false,
};

const cloneValue = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const setNestedValue = <T extends object>(state: T, path: string, value: any): T => {
  const nextState = cloneValue(state);
  const keys = path.split(".");
  let cursor: any = nextState;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }

    cursor[key] = cursor[key] || {};
    cursor = cursor[key];
  });

  return nextState;
};

const getErrorByPath = (errors: any, path: string): string => {
  const keys = path.split(".");
  let cursor = errors;

  for (let index = 0; index < keys.length; index += 1) {
    if (!cursor) {
      return "";
    }

    cursor = cursor[keys[index]];
  }

  return typeof cursor === "string" ? cursor : "";
};

const hasSavedRBPayRecord = (state: RBPaySettingsState) =>
  Boolean(state.meta.updatedAt) || state.approvalInfo.status !== "not-started";

// A background account refresh (e.g. the worker just added their SIN/Business on the
// SIN/Business page and navigated back) must NOT discard edits already typed into this
// form. Reconcile instead of reset: adopt the freshly-saved account-owned identity
// (pharma SIN/Business) plus the server-owned approval/lifecycle, but keep every field
// the worker enters here (payment method, bank details, EMT email, authorizations).
const reconcileDraftWithServer = (
  currentDraft: RBPaySettingsState,
  settings: RBPaySettingsState
): RBPaySettingsState => {
  const next = cloneRBPaySettingsState(currentDraft);
  const isPharma = settings.industryMode === IndustryModeEnum.Pharmaceutical;

  // Server-owned lifecycle always refreshes to the latest.
  next.industryMode = settings.industryMode;
  next.workerEmail = settings.workerEmail;
  next.approvalInfo = settings.approvalInfo;
  next.dashboard = settings.dashboard;

  // Pharma SIN/Business lives on the account, not this form, so adopt the just-saved
  // values. Dentistry owns these fields in-form, so keep the worker's in-progress edits.
  if (isPharma) {
    next.employmentType = settings.employmentType;
    next.employeeProfile = {
      ...next.employeeProfile,
      sinNumber: settings.employeeProfile.sinNumber,
    };
    next.businessProfile = {
      ...settings.businessProfile,
      certificationAccepted: next.businessProfile.certificationAccepted,
    };
  }

  // A DOB typed into this form is the worker's in-progress edit and must survive a
  // background refresh. An EMPTY one means the draft never had a value — including
  // a draft cached before this field existed — so adopt the server/account value.
  if (!next.employeeProfile.dateOfBirth) {
    next.employeeProfile = {
      ...next.employeeProfile,
      dateOfBirth: settings.employeeProfile.dateOfBirth,
    };
  }

  return next;
};

// The in-progress draft is cached across the SIN/Business round-trip. On web the RB
// Pay page and the SIN/Business page are sibling routes, so navigating to add a SIN
// UNMOUNTS this page and destroys the draft. We stash the draft when leaving for the
// SIN page and restore it on the next mount so the worker's payment edits survive.
// (Mobile keeps the screen mounted in the navigation stack, so it needs none of this.)
const DRAFT_CACHE_KEY_PREFIX = "rbPayDraftCache:";
const draftCacheKey = (workerId: string) =>
  `${DRAFT_CACHE_KEY_PREFIX}${workerId || "current"}`;

const saveDraftCache = (workerId: string, draft: RBPaySettingsState) => {
  try {
    window.sessionStorage.setItem(draftCacheKey(workerId), JSON.stringify(draft));
  } catch (error) {
    // sessionStorage unavailable (private mode / quota) — degrade to no persistence.
  }
};

const readDraftCache = (workerId: string): RBPaySettingsState | null => {
  try {
    const raw = window.sessionStorage.getItem(draftCacheKey(workerId));
    return raw ? (JSON.parse(raw) as RBPaySettingsState) : null;
  } catch (error) {
    return null;
  }
};

const clearDraftCache = (workerId: string) => {
  try {
    window.sessionStorage.removeItem(draftCacheKey(workerId));
  } catch (error) {
    // no-op
  }
};

interface UseRBPaySetupArgs {
  account: any;
  industryMode?: string | null;
}

export const useRBPaySetup = ({ account, industryMode }: UseRBPaySetupArgs) => {
  const [serverState, setServerState] = useState<RBPaySettingsState | null>(null);
  const [networkConfiguration, setNetworkConfiguration] = useState<RBPayNetworkConfiguration | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const [draft, setDraft] = useState<RBPaySettingsState | null>(null);
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // RB Pay is a single application: the whole form is either in read-only view
  // mode or fully editable. (Replaces the old per-section editing model.)
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [documentUploadError, setDocumentUploadError] = useState("");

  const [verificationState, setVerificationState] = useState<RBPayVerificationState>(defaultVerificationState);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  // Distinguishes the first load (full reset + loader) from later account refreshes
  // (silent reconcile that preserves the worker's in-progress edits).
  const hasLoadedOnceRef = useRef(false);

  const defaultState = useMemo(
    () => createDefaultRBPaySettingsState(account, industryMode),
    [account, industryMode]
  );

  const activeServerState = serverState || defaultState;
  const activeDraft = draft || cloneRBPaySettingsState(activeServerState);

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendSecondsLeft((currentValue) => Math.max(0, currentValue - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendSecondsLeft]);

  const loadState = useCallback(async () => {
    if (!account) {
      return;
    }

    // Only the very first load blocks the page with the loader and adopts server state
    // wholesale. Later account refreshes (e.g. returning from the SIN/Business page)
    // reconcile silently so the worker's in-progress edits stay on screen.
    const isInitialLoad = !hasLoadedOnceRef.current;
    hasLoadedOnceRef.current = true;

    if (isInitialLoad) {
      setIsPageLoading(true);
    }

    try {
      const [settings, config] = await Promise.all([
        rbPaySettingsService.getWorkerRBPaySettings({ account, industryMode }),
        rbPaySettingsService.getRBPayNetworkConfiguration(account),
      ]);

      setServerState(settings);
      setNetworkConfiguration(config);

      // On the first mount, restore a draft stashed before leaving for the SIN/Business
      // page (see preserveDraftForReturn) so the worker's in-progress edits survive the
      // route change. Merge it with the freshly-loaded server/account identity, then
      // consume the cache so it never resurrects on a later, unrelated visit.
      const cachedDraft = isInitialLoad ? readDraftCache(settings.workerId) : null;
      if (cachedDraft) {
        clearDraftCache(settings.workerId);
      }

      setDraft((currentDraft) => {
        if (isInitialLoad) {
          return cachedDraft
            ? reconcileDraftWithServer(cachedDraft, settings)
            : cloneRBPaySettingsState(settings);
        }
        return currentDraft
          ? reconcileDraftWithServer(currentDraft, settings)
          : cloneRBPaySettingsState(settings);
      });

      if (isInitialLoad) {
        setIsEditing(false);
        setSubmitAttempted(false);
        setTouchedFields({});
      }
      setErrorMessage("");
    } catch (error) {
      const err: any = error;
      setErrorMessage(err?.message || "Unable to load RB Pay settings.");
    } finally {
      if (isInitialLoad) {
        setIsPageLoading(false);
      }
    }
  }, [account, industryMode]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const hasRecord = hasSavedRBPayRecord(activeServerState);
  const mode: RBPaySetupMode = hasRecord ? "manage" : "setup";

  const activeIndustryMode =
    activeDraft.industryMode ||
    activeServerState.industryMode ||
    industryMode ||
    IndustryModeEnum.Dentistry;
  const industryConfig =
    RB_PAY_INDUSTRY_CONFIG[activeIndustryMode as keyof typeof RB_PAY_INDUSTRY_CONFIG] ||
    RB_PAY_INDUSTRY_CONFIG[IndustryModeEnum.Dentistry];
  const supportsProfileTypeSelection = industryConfig.supportsProfileTypeSelection;

  // Pharma manages SIN/Business on the SIN/Business Settings page (stored on the
  // account), not in this form. RB Pay still requires at least one on file before
  // it can be saved, so a missing value blocks Save and marks Profile incomplete.
  const pharmaSinOrBusinessSet = Boolean(
    String(account?.pharmacist?.socialInsuranceNumber || "").trim() ||
      String(account?.pharmacist?.taxNumber || "").trim()
  );
  const pharmaProfileMissing = !supportsProfileTypeSelection && !pharmaSinOrBusinessSet;

  const profileErrors = useMemo(
    () => validateRBPayProfileSection(buildRBPayProfileSectionDraft(activeDraft)),
    [activeDraft]
  );
  const paymentErrors = useMemo(
    () => validateRBPayPaymentSection(buildRBPayPaymentSectionDraft(activeDraft)),
    [activeDraft]
  );

  const isDirectDeposit = activeDraft.paymentMethod === RBPayPaymentMethod.DIRECT_DEPOSIT;

  // Pharma skips the rest of the profile validation (SIN/business live on the
  // account), but Date of Birth IS collected in this form and the backend requires
  // it for every employee profile, pharma included — so it must gate Save on both
  // industries. Tracked separately since pharma otherwise ignores profileErrors.
  const dateOfBirthError = profileErrors.employeeProfile?.dateOfBirth || "";

  // Step completeness. Authorization is its own step, so payment "core" excludes it.
  const profileComplete = supportsProfileTypeSelection
    ? !hasValidationErrors(profileErrors)
    : pharmaSinOrBusinessSet && !dateOfBirthError;
  // Empty direct-deposit now produces "required" errors (empty is no longer
  // treated as cleared) so the step never shows complete; an all-zero section is
  // the explicit "remove" state, validates cleanly, and reads as complete.
  const paymentCoreComplete = isDirectDeposit
    ? !paymentErrors.directDeposit?.institutionNumber &&
      !paymentErrors.directDeposit?.transitNumber &&
      !paymentErrors.directDeposit?.accountNumber
    : !paymentErrors.emailTransfer?.email && !paymentErrors.emailTransfer?.emailVerified;
  const authComplete = isDirectDeposit
    ? activeDraft.directDeposit.authorizationAccepted
    : activeDraft.emailTransfer.certificationAccepted;

  const steps = useMemo(() => {
    const builtSteps: {
      key: RBPaySetupSectionKey;
      label: string;
      caption: string;
      complete: boolean;
    }[] = [];

    if (supportsProfileTypeSelection) {
      builtSteps.push({
        key: "profile",
        label: "Profile Information",
        caption: "Payroll type & tax",
        complete: profileComplete,
      });
    }

    builtSteps.push({
      key: "payment",
      label: "Payment Method",
      caption: "Bank or e-transfer",
      complete: paymentCoreComplete,
    });

    builtSteps.push({
      key: "authorization",
      label: "Authorization",
      caption: "Review & authorize",
      complete: authComplete,
    });

    return builtSteps;
  }, [authComplete, paymentCoreComplete, profileComplete, supportsProfileTypeSelection]);

  const isSetupComplete = steps.every((step) => step.complete);
  const activeStepKey = (steps.find((step) => !step.complete) || steps[steps.length - 1])?.key;

  const hasBlockingErrors =
    (supportsProfileTypeSelection && hasValidationErrors(profileErrors)) ||
    pharmaProfileMissing ||
    Boolean(dateOfBirthError) ||
    hasValidationErrors(paymentErrors);

  const updateDraft = useCallback(
    (path: string, value: any) => {
      setDraft((currentDraft) => {
        const baseDraft = currentDraft || cloneRBPaySettingsState(activeServerState);
        let nextDraft = setNestedValue(baseDraft, path, value);

        // Profile side effects
        if (path === "employeeProfile.sinNumber") {
          nextDraft = setNestedValue(nextDraft, path, formatSin(value));
        }

        if (path === "businessProfile.businessNumber") {
          // Keystroke mask: digits only, RT/RC locked by the selected business type.
          nextDraft = setNestedValue(
            nextDraft,
            path,
            maskBusinessNumber(
              value,
              baseDraft.businessProfile.businessType,
              baseDraft.businessProfile.businessNumber
            )
          );
        }

        if (path === "employmentType" && value !== baseDraft.employmentType) {
          nextDraft = setNestedValue(nextDraft, "businessProfile.certificationAccepted", false);
          // Entering the Business profile seeds the canonical default Business #
          // (000-000-000 RT0001 / RC0001) when none is present — matches RB-Portal.
          if (
            value === RBPayEmploymentType.BUSINESS &&
            !normalizeDigits(baseDraft.businessProfile.businessNumber)
          ) {
            nextDraft = setNestedValue(
              nextDraft,
              "businessProfile.businessNumber",
              defaultBusinessNumber(baseDraft.businessProfile.businessType)
            );
          }
        }

        // Lock the RT/RC program-account code to the selected business type,
        // recomposing the canonical value (default 000-000-000 RT/RC 0001 when empty).
        if (path === "businessProfile.businessType" && value !== baseDraft.businessProfile.businessType) {
          nextDraft = setNestedValue(
            nextDraft,
            "businessProfile.businessNumber",
            composeBusinessNumber(baseDraft.businessProfile.businessNumber, value) ||
              defaultBusinessNumber(value)
          );
        }

        if (
          path === "businessProfile.businessType" ||
          path === "businessProfile.legalBusinessName" ||
          path === "businessProfile.businessNumber"
        ) {
          nextDraft = setNestedValue(nextDraft, "businessProfile.certificationAccepted", false);
        }

        // Payment side effects
        if (path === "paymentMethod" && value !== baseDraft.paymentMethod) {
          nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
          nextDraft = setNestedValue(nextDraft, "emailTransfer.certificationAccepted", false);
        }

        if (path === "directDeposit.institutionNumber") {
          nextDraft = setNestedValue(nextDraft, path, normalizeDigits(value).slice(0, 3));
          nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
        }

        if (path === "directDeposit.transitNumber") {
          nextDraft = setNestedValue(nextDraft, path, normalizeDigits(value).slice(0, 5));
          nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
        }

        if (path === "directDeposit.accountNumber") {
          nextDraft = setNestedValue(nextDraft, path, normalizeDigits(value).slice(0, 12));
          nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
        }

        if (path === "directDeposit.document") {
          nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
        }

        if (path === "emailTransfer.email") {
          const normalizedEmail = String(value || "").trim().toLowerCase();
          const savedVerifiedEmail = activeServerState.emailTransfer.verifiedEmail || "";
          const matchesSaved = Boolean(savedVerifiedEmail) && normalizedEmail === savedVerifiedEmail;
          nextDraft = setNestedValue(nextDraft, path, normalizedEmail);
          nextDraft = setNestedValue(nextDraft, "emailTransfer.certificationAccepted", false);
          nextDraft = setNestedValue(nextDraft, "emailTransfer.emailVerified", matchesSaved);
          nextDraft = setNestedValue(
            nextDraft,
            "emailTransfer.verifiedEmail",
            matchesSaved ? savedVerifiedEmail : ""
          );
        }

        return nextDraft;
      });
    },
    [activeServerState]
  );

  const markFieldTouched = useCallback((path: string) => {
    setTouchedFields((currentValue) => ({
      ...currentValue,
      [path]: true,
    }));
  }, []);

  const getFieldError = useCallback(
    (path: string) => {
      if (!submitAttempted && !touchedFields[path]) {
        return "";
      }

      const profileError = getErrorByPath(profileErrors, path);
      if (profileError) {
        return profileError;
      }

      return getErrorByPath(paymentErrors, path);
    },
    [paymentErrors, profileErrors, submitAttempted, touchedFields]
  );

  const startEdit = useCallback(() => {
    setDraft(cloneRBPaySettingsState(activeServerState));
    setIsEditing(true);
    setSubmitAttempted(false);
    setTouchedFields({});
    setErrorMessage("");
    setDocumentUploadError("");
  }, [activeServerState]);

  const cancelEdit = useCallback(() => {
    clearDraftCache(activeServerState.workerId);
    setDraft(cloneRBPaySettingsState(activeServerState));
    setIsEditing(false);
    setSubmitAttempted(false);
    setTouchedFields({});
    setErrorMessage("");
    setDocumentUploadError("");
    setVerificationState(defaultVerificationState);
    setResendSecondsLeft(0);
  }, [activeServerState]);

  // Stash the current draft before navigating to the SIN/Business page so the worker's
  // in-progress edits survive the route change that unmounts this page (web only).
  const preserveDraftForReturn = useCallback(() => {
    saveDraftCache(activeDraft.workerId, activeDraft);
  }, [activeDraft]);

  const persist = useCallback(
    async (stateToSave: RBPaySettingsState): Promise<boolean> => {
      setIsSaving(true);
      setErrorMessage("");

      try {
        const savedState = await rbPaySettingsService.saveWorkerRBPaySettings({
          account,
          settings: stateToSave,
          industryMode,
        });

        clearDraftCache(savedState.workerId);
        setServerState(savedState);
        setDraft(cloneRBPaySettingsState(savedState));
        setIsEditing(false);
        setSubmitAttempted(false);
        setTouchedFields({});
        setDocumentUploadError("");
        window.dispatchEvent(new CustomEvent("rb-pay-settings-updated"));
        return true;
      } catch (error) {
        const err: any = error;
        setErrorMessage(err?.message || "Unable to save RB Pay settings.");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [account, industryMode]
  );

  const requestVerificationCode = useCallback(
    async (pendingSave: boolean) => {
      const email = String(activeDraft.emailTransfer.email || "").trim();

      setVerificationState((currentValue) => ({
        ...currentValue,
        open: true,
        pendingSave,
        email,
        code: "",
        errorMessage: "",
        successMessage: "",
        isRequesting: true,
      }));

      try {
        await rbPaySettingsService.requestEmailVerificationCode(account, email);
        setVerificationState((currentValue) => ({
          ...currentValue,
          open: true,
          pendingSave,
          email,
          errorMessage: "",
          successMessage: "Verification code sent.",
          isRequesting: false,
        }));
        setResendSecondsLeft(30);
      } catch (error) {
        const err: any = error;
        setVerificationState((currentValue) => ({
          ...currentValue,
          open: true,
          pendingSave,
          errorMessage: err?.message || "Unable to send verification code.",
          successMessage: "",
          isRequesting: false,
        }));
      }
    },
    [account, activeDraft.emailTransfer.email]
  );

  // Reports the precise outcome so the page can show the success modal, scroll
  // to the first invalid section, or raise an error alert.
  const save = useCallback(async (): Promise<RBPaySaveResult> => {
    setSubmitAttempted(true);

    if (hasBlockingErrors) {
      return "invalid";
    }

    const nextEmail = String(activeDraft.emailTransfer.email || "").trim();
    const savedEmail = String(
      activeServerState.emailTransfer.verifiedEmail ||
        activeServerState.emailTransfer.email ||
        activeServerState.emailTransfer.shadowEmail ||
        activeServerState.workerEmail
    ).trim();
    const emailChanged =
      activeDraft.paymentMethod === RBPayPaymentMethod.EMAIL_TRANSFER && nextEmail !== savedEmail;

    if (emailChanged && !activeDraft.emailTransfer.emailVerified) {
      await requestVerificationCode(true);
      return "pending-verification";
    }

    const persisted = await persist(activeDraft);
    return persisted ? "saved" : "error";
  }, [activeDraft, activeServerState, hasBlockingErrors, persist, requestVerificationCode]);

  const uploadDocument = useCallback(
    async (file: File) => {
      const document = await rbPaySettingsService.uploadWorkerRBPayDocument(account, file);
      setDraft((currentDraft) => {
        const baseDraft = currentDraft || cloneRBPaySettingsState(activeServerState);
        let nextDraft = setNestedValue(baseDraft, "directDeposit.document", document);
        nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
        return nextDraft;
      });
      markFieldTouched("directDeposit.document");
    },
    [account, activeServerState, markFieldTouched]
  );

  const removeDocument = useCallback(() => {
    setDraft((currentDraft) => {
      const baseDraft = currentDraft || cloneRBPaySettingsState(activeServerState);
      let nextDraft = setNestedValue(baseDraft, "directDeposit.document", null);
      nextDraft = setNestedValue(nextDraft, "directDeposit.authorizationAccepted", false);
      return nextDraft;
    });
    // Surface the "banking document is required" error right away instead of
    // waiting for a save attempt.
    markFieldTouched("directDeposit.document");
  }, [activeServerState, markFieldTouched]);

  const closeVerificationModal = useCallback(() => {
    const fallbackEmail =
      activeServerState.emailTransfer.verifiedEmail ||
      activeServerState.emailTransfer.email ||
      activeServerState.emailTransfer.shadowEmail ||
      activeServerState.workerEmail;

    setDraft((currentDraft) => {
      const baseDraft = currentDraft || cloneRBPaySettingsState(activeServerState);
      const nextDraft = cloneValue(baseDraft);
      nextDraft.emailTransfer.email = fallbackEmail;
      nextDraft.emailTransfer.emailVerified = Boolean(activeServerState.emailTransfer.verifiedEmail);
      nextDraft.emailTransfer.verifiedEmail = activeServerState.emailTransfer.verifiedEmail || "";
      nextDraft.emailTransfer.certificationAccepted = false;
      return nextDraft;
    });

    setVerificationState(defaultVerificationState);
    setResendSecondsLeft(0);
  }, [activeServerState]);

  const submitVerificationCode = useCallback(async () => {
    setVerificationState((currentValue) => ({
      ...currentValue,
      isVerifying: true,
      errorMessage: "",
      successMessage: "",
    }));

    try {
      await rbPaySettingsService.verifyEmailVerificationCode(account, verificationState.code);

      const verifiedDraft = cloneValue(activeDraft);
      verifiedDraft.emailTransfer.email = verificationState.email;
      verifiedDraft.emailTransfer.verifiedEmail = verificationState.email;
      verifiedDraft.emailTransfer.emailVerified = true;

      setDraft(verifiedDraft);
      setVerificationState((currentValue) => ({
        ...currentValue,
        isVerifying: false,
        successMessage: "Email verified successfully.",
      }));

      if (verificationState.pendingSave) {
        await persist(verifiedDraft);
      }

      window.setTimeout(() => {
        setVerificationState(defaultVerificationState);
        setResendSecondsLeft(0);
      }, 600);
    } catch (error) {
      const err: any = error;
      setVerificationState((currentValue) => ({
        ...currentValue,
        isVerifying: false,
        errorMessage: err?.message || "Unable to verify email code.",
      }));
    }
  }, [
    account,
    activeDraft,
    persist,
    verificationState.code,
    verificationState.email,
    verificationState.pendingSave,
  ]);

  const setVerificationCode = useCallback((code: string) => {
    setVerificationState((currentValue) => ({
      ...currentValue,
      code,
      errorMessage: "",
      successMessage: "",
    }));
  }, []);

  // The first section (in display order) that still has a blocking error, so
  // the page can scroll the user there on a failed Save. Profile errors and the
  // business certification belong to Profile; bank/email/verification to
  // Payment; the deposit-authorization / email-certification checkbox to
  // Authorization.
  const getFirstInvalidSection = useCallback((): RBPaySetupSectionKey | null => {
    if (
      (supportsProfileTypeSelection && hasValidationErrors(profileErrors)) ||
      pharmaProfileMissing ||
      dateOfBirthError
    ) {
      return "profile";
    }

    const ddErrors = paymentErrors.directDeposit || {};
    const emailErrors = paymentErrors.emailTransfer || {};

    const paymentInvalid = isDirectDeposit
      ? Boolean(
          paymentErrors.paymentMethod ||
            ddErrors.institutionNumber ||
            ddErrors.transitNumber ||
            ddErrors.accountNumber
        )
      : Boolean(paymentErrors.paymentMethod || emailErrors.email || emailErrors.emailVerified);
    if (paymentInvalid) {
      return "payment";
    }

    const authInvalid = isDirectDeposit
      ? Boolean(ddErrors.authorizationAccepted)
      : Boolean(emailErrors.certificationAccepted);
    if (authInvalid) {
      return "authorization";
    }

    return null;
  }, [
    dateOfBirthError,
    isDirectDeposit,
    paymentErrors,
    pharmaProfileMissing,
    profileErrors,
    supportsProfileTypeSelection,
  ]);

  // Has the worker actually changed anything vs the saved record? Used to block
  // a pointless resubmission when editing with no changes. (meta/approvalInfo
  // aren't touched while editing, so a full-state compare is reliable.)
  const isDirty = useMemo(
    () => JSON.stringify(activeServerState) !== JSON.stringify(activeDraft),
    [activeServerState, activeDraft]
  );

  // Save stays clickable even when incomplete so the user gets a guided flow:
  // clicking validates, reveals errors, and scrolls to the first invalid
  // section (handled on the page). Persistence itself is still blocked by
  // `hasBlockingErrors` inside `save()`. When editing an existing record we
  // also disable Save if nothing changed, to prevent a no-op resubmission.
  const isSaveDisabled =
    isSaving ||
    verificationState.isRequesting ||
    verificationState.isVerifying ||
    (isEditing && !isDirty);

  return {
    isPageLoading,
    mode,
    serverState: activeServerState,
    draft: activeDraft,
    networkConfiguration,
    statementsUrl: networkConfiguration?.statementsUrl || "",
    contactEmail:
      networkConfiguration?.contactEmail || localStorage.getItem("contactEmail") || RBAdminEmail,
    activeIndustryMode,
    industryConfig,
    supportsProfileTypeSelection,
    pharmaProfileMissing,
    steps,
    activeStepKey,
    isSetupComplete,
    isEditing,
    isDirty,
    isSaving,
    isSaveDisabled,
    getFirstInvalidSection,
    errorMessage,
    documentUploadError,
    setDocumentUploadError,
    startEdit,
    cancelEdit,
    preserveDraftForReturn,
    save,
    updateDraft,
    markFieldTouched,
    getFieldError,
    uploadDocument,
    removeDocument,
    verificationState,
    resendSecondsLeft,
    requestVerificationCode: () => requestVerificationCode(false),
    closeVerificationModal,
    submitVerificationCode,
    setVerificationCode,
  };
};
